import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
  type SilentRequest,
} from '@azure/msal-browser';
import { config } from '../config';
import type { AuthMethod } from '../types';
import { log, logError, redactToken } from './logger';

let msalInstance: IPublicClientApplication | null = null;

export class AuthError extends Error {
  readonly code:
    | 'config'
    | 'silent_failed'
    | 'interactive_failed'
    | 'consent_required'
    | 'network'
    | 'unknown';
  readonly detail?: string;

  constructor(
    message: string,
    code: AuthError['code'],
    detail?: string,
  ) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.detail = detail;
  }
}

export async function initMsal(): Promise<IPublicClientApplication> {
  if (msalInstance) return msalInstance;

  const msalConfig = {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      redirectUri: config.appUrl,
    },
    cache: {
      cacheLocation: 'sessionStorage' as const,
    },
  };

  log('MSAL NAA initialisieren', {
    clientIdPrefix: config.clientId.slice(0, 8),
    authority: config.authority,
  });

  msalInstance = await createNestablePublicClientApplication(msalConfig);
  return msalInstance;
}

export interface TokenResult {
  accessToken: string;
  method: AuthMethod;
}

/**
 * Primary path: Nested App Authentication (silent via Outlook host broker).
 * Fallback: interactive popup only when InteractionRequiredAuthError.
 */
export async function acquireGraphToken(): Promise<TokenResult> {
  const pca = await initMsal();
  const tokenRequest: SilentRequest = {
    scopes: config.graphScopes,
  };

  try {
    const result = await pca.acquireTokenSilent(tokenRequest);
    log('SSO Token erhalten (silent/NAA)', redactToken(result.accessToken));
    return { accessToken: result.accessToken, method: 'naa-silent' };
  } catch (silentError) {
    logError('Silent token acquisition failed', silentError);

    if (silentError instanceof InteractionRequiredAuthError) {
      try {
        log('Interactive Consent/Login Fallback (Popup)…');
        const result = await pca.acquireTokenPopup({
          scopes: config.graphScopes,
        });
        log('SSO Token erhalten (interactive)', redactToken(result.accessToken));
        return { accessToken: result.accessToken, method: 'naa-interactive' };
      } catch (popupError) {
        const msg = errorMessage(popupError);
        if (/consent|AADSTS65001|AADSTS65004/i.test(msg)) {
          throw new AuthError(
            'Microsoft Consent erforderlich. Bitte Admin Consent in Entra ID erteilen oder dem Dialog zustimmen.',
            'consent_required',
            msg,
          );
        }
        if (/redirect|AADSTS50011/i.test(msg)) {
          throw new AuthError(
            'Redirect URI stimmt nicht. In Entra unter Authentication SPA-Redirects prüfen (https://signatur.sundos.de und brk-multihub://signatur.sundos.de).',
            'config',
            msg,
          );
        }
        if (/AADSTS700016|application/i.test(msg)) {
          throw new AuthError(
            'Client ID oder Tenant ID scheint falsch. Bitte VITE_CLIENT_ID / VITE_TENANT_ID prüfen.',
            'config',
            msg,
          );
        }
        throw new AuthError(
          'Interaktive Microsoft-Anmeldung fehlgeschlagen.',
          'interactive_failed',
          msg,
        );
      }
    }

    const msg = errorMessage(silentError);
    if (/network|fetch|Failed to fetch/i.test(msg)) {
      throw new AuthError('Netzwerkfehler bei der Authentifizierung.', 'network', msg);
    }
    throw new AuthError(
      'SSO-Token konnte nicht bezogen werden. Outlook-Anmeldung und Entra-Konfiguration prüfen.',
      'silent_failed',
      msg,
    );
  }
}

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}
